import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Chip, 
  Button, 
  CircularProgress, 
  Snackbar,
  Alert,
  Avatar,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Zoom,
  IconButton,
  TextField,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { Idea, Comment } from '../types';
import { api } from '../services/api';
import { formatDate } from '../utils/formatDate';
import CommentsSection from '../components/CommentsSection';
import { useAuth } from '../contexts/AuthContext';
import { ADMIN_EMAILS } from '../constants'; // Import from constants

const TEST_USER_ID = process.env.REACT_APP_TEST_USER_ID || "00000000-0000-0000-0000-000000000001";

// Add custom animation keyframes
const customAnimations = `
@keyframes floatUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
}
`;

const getAvatarColor = (name: string) => {
  const colors = [
    '#e57373', '#f06292', '#ba68c8', '#9575cd', 
    '#7986cb', '#64b5f6', '#4fc3f7', '#4dd0e1', 
    '#4db6ac', '#81c784', '#aed581', '#dce775',
    '#fff176', '#ffd54f', '#ffb74d', '#ff8a65'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash |= 0;
  }
  
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

// Generate placeholder gradient based on id
const getPlaceholderGradient = (id: string) => {
  const colors = [
    ['#ff9a9e', '#fad0c4'],
    ['#a18cd1', '#fbc2eb'],
    ['#ffecd2', '#fcb69f'],
    ['#ff9a9e', '#fecfef'],
    ['#f6d365', '#fda085'],
    ['#84fab0', '#8fd3f4'],
    ['#a6c0fe', '#f68084'],
    ['#fccb90', '#d57eeb'],
    ['#e0c3fc', '#8ec5fc'],
    ['#f093fb', '#f5576c']
  ];
  
  // Use idea id to deterministically choose a gradient
  let hashValue = 0;
  for (let i = 0; i < id.length; i++) {
    hashValue = id.charCodeAt(i) + ((hashValue << 5) - hashValue);
  }
  
  const colorIndex = Math.abs(hashValue) % colors.length;
  return `linear-gradient(135deg, ${colors[colorIndex][0]} 0%, ${colors[colorIndex][1]} 100%)`;
};

const IdeaDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, currentUser, databaseUserId } = useAuth();
  const queryClient = useQueryClient();
  
  // Helper function to check if the current user is an admin
  const isCurrentUserAdmin = (): boolean => {
    return !!currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);
  };

  // Add console logs to check what databaseUserId contains
  console.log('IdeaDetail - Auth values:', { 
    isAuthenticated, 
    currentUser: currentUser ? { uid: currentUser.uid, email: currentUser.email } : null,
    databaseUserId
  });
  
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editing, setEditing] = useState(false);

  // Fetch idea details with React Query
  const {
    data: idea,
    isLoading: loading,
    error: ideaError,
  } = useQuery({
    queryKey: ['idea', id, currentUser?.uid],
    queryFn: async () => {
      if (!id) return null;
      
      const uid = isAuthenticated && currentUser ? currentUser.uid : undefined;
      const userId = uid ? await api.getUserDatabaseUUID(uid) : undefined;
      
      if (!userId) {
        console.error('Failed to convert Firebase UID to database UUID');
        return api.getIdea(id);
      } else {
        return api.getIdea(id, userId);
      }
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes for individual ideas
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Fetch comments with React Query
  const {
    data: comments = [],
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useQuery({
    queryKey: ['comments', id],
    queryFn: () => id ? api.getIdeaComments(id) : [],
    enabled: !!id && !!idea,
    staleTime: 1 * 60 * 1000, // 1 minute for comments
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  const error = ideaError ? String(ideaError) : (!idea && !loading ? 'Idea not found.' : null);

  // Animate in components after initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateIn(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Upvote mutation
  const upvoteMutation = useMutation({
    mutationFn: (ideaId: string) => api.toggleUpvote(ideaId, idea?.liked_by_user === true),
    onSuccess: (updatedIdea) => {
      // Update the idea cache
      queryClient.setQueryData(['idea', id, currentUser?.uid], updatedIdea);
      
      // Invalidate ideas list to keep it in sync
      queryClient.invalidateQueries({ queryKey: ['ideas'] });
      
      setSnackbar({
        open: true,
        message: updatedIdea?.liked_by_user ? 'Upvote added!' : 'Upvote removed',
        severity: 'success'
      });
    },
    onError: (err) => {
      console.error('Failed to upvote:', err);
      setSnackbar({
        open: true,
        message: 'Failed to register your vote.',
        severity: 'error'
      });
    },
  });

  const handleUpvote = () => {
    if (!idea || !currentUser) {
      setSnackbar({
        open: true,
        message: 'Please log in to vote on ideas',
        severity: 'error'
      });
      return;
    }
    
    upvoteMutation.mutate(idea.id);
  };

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: ({ ideaId, text, isAnonymous }: { ideaId: string; text: string; isAnonymous?: boolean }) =>
      api.addComment({ idea_id: ideaId, comment_text: text, is_anonymous: isAnonymous }),
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: ['comments', id] });
      
      // Update idea comment count optimistically
      if (idea) {
        queryClient.setQueryData(['idea', id, currentUser?.uid], {
          ...idea,
          comment_count: idea.comment_count + 1
        });
      }
    },
    onError: (err) => {
      console.error('Failed to add comment:', err);
    },
  });

  // Delete comment mutation
  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(commentId, id!),
    onSuccess: () => {
      // Invalidate and refetch comments
      queryClient.invalidateQueries({ queryKey: ['comments', id] });
      
      // Update idea comment count optimistically
      if (idea) {
        queryClient.setQueryData(['idea', id, currentUser?.uid], {
          ...idea,
          comment_count: Math.max(0, idea.comment_count - 1)
        });
      }
      
      setSnackbar({
        open: true,
        message: 'Comment deleted successfully',
        severity: 'success'
      });
    },
    onError: (err) => {
      console.error('Failed to delete comment:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete comment. Please try again.',
        severity: 'error'
      });
    },
  });

  const handleAddComment = async (ideaId: string, text: string, isAnonymous?: boolean) => {
    try {
      await addCommentMutation.mutateAsync({ ideaId, text, isAnonymous });
      return Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    deleteCommentMutation.mutate(commentId);
  };

  const handleDeleteIdea = () => {
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!idea) return;
    
    try {
      setDeleting(true);
      await api.deleteIdea(idea.id);
      setDeleteDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Idea deleted successfully',
        severity: 'success'
      });
      
      // Navigate back to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      console.error('Failed to delete idea:', err);
      setDeleting(false);
      setDeleteDialogOpen(false);
      
      setSnackbar({
        open: true,
        message: 'Failed to delete idea. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
  };

  const handleBack = () => {
    navigate('/home');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenImageDialog = () => {
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedImage(event.target.files[0]);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImage || !idea) return;
    
    try {
      setUploadingImage(true);
      
      // Make API call to update image
      const updatedIdea = await api.uploadIdeaImage(idea.id, selectedImage);
      
      // Update the cached idea with the new image URL
      if (updatedIdea) {
        queryClient.setQueryData(['idea', id, currentUser?.uid], updatedIdea);
      }
      
      setSnackbar({
        open: true,
        message: 'Image updated successfully',
        severity: 'success'
      });
      
      handleCloseImageDialog();
    } catch (err) {
      console.error('Failed to update image:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update image. Please try again.',
        severity: 'error'
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleOpenEditDialog = () => {
    if (!idea) return;
    setEditTitle(idea.title);
    setEditDescription(idea.description || '');
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditTitle('');
    setEditDescription('');
  };

  const handleEditIdea = async () => {
    if (!idea || !editTitle.trim()) {
      setSnackbar({
        open: true,
        message: 'Title is required',
        severity: 'error'
      });
      return;
    }
    
    try {
      setEditing(true);
      
      // Make API call to edit idea
      const updatedIdea = await api.editIdea(idea.id, {
        title: editTitle.trim(),
        description: editDescription.trim()
      });
      
      // Update the cached idea with the new content
      if (updatedIdea) {
        queryClient.setQueryData(['idea', id, currentUser?.uid], updatedIdea);
        // Invalidate ideas list to keep it in sync
        queryClient.invalidateQueries({ queryKey: ['ideas'] });
      }
      
      setSnackbar({
        open: true,
        message: 'Idea updated successfully',
        severity: 'success'
      });
      
      handleCloseEditDialog();
    } catch (err) {
      console.error('Failed to update idea:', err);
      setSnackbar({
        open: true,
        message: 'Failed to update idea. Please try again.',
        severity: 'error'
      });
    } finally {
      setEditing(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '50vh', justifyContent: 'center' }}>
        <style>{customAnimations}</style>
        <CircularProgress size={70} thickness={4} sx={{ color: 'secondary.main', mb: 3, animation: 'pulse 1.5s infinite' }} />
        <Typography variant="h6" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
          Loading idea details...
        </Typography>
      </Container>
    );
  }

  if (error || !idea) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <style>{customAnimations}</style>
        <Fade in={true} timeout={800}>
          <Box>
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3, 
                borderRadius: 2, 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                animation: 'floatUp 0.5s ease forwards'
              }}
            >
              {error || 'Idea not found.'}
            </Alert>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Back to Ideas
            </Button>
          </Box>
        </Fade>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <style>{customAnimations}</style>
      <Fade in={animateIn} timeout={800}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateX(-5px)',
                  backgroundColor: 'rgba(0,0,0,0.04)',
                }
              }}
            >
              Back to Ideas
            </Button>
            
            {/* Only show edit and delete buttons if user is admin */}
            {isCurrentUserAdmin() && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={handleOpenEditDialog}
                  sx={{
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(25, 118, 210, 0.2)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(25, 118, 210, 0.1)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.5s ease',
                    },
                    '&:hover::after': {
                      transform: 'translateX(0)'
                    }
                  }}
                >
                  Edit Idea
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteIdea}
                  sx={{
                    borderRadius: 2,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(244,67,54,0.2)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(244,67,54,0.1)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.5s ease',
                    },
                    '&:hover::after': {
                      transform: 'translateX(0)'
                    }
                  }}
                >
                  Delete Idea
                </Button>
              </Box>
            )}
          </Box>
          
          <Zoom in={animateIn} timeout={500}>
            <Paper 
              elevation={2} 
              sx={{ 
                p: { xs: 2, md: 4 }, 
                borderRadius: 2,
                position: 'relative',
                overflow: 'hidden',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 12px 28px rgba(0,0,0,0.12)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: (theme) => 
                    `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                }
              }}
            >
              <Typography 
                variant="h4" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 700,
                  position: 'relative',
                  mb: 3,
                  display: 'inline-block',
                }}
              >
                {idea.title}
              </Typography>
              
              {/* Display image if available, or placeholder */}
              <Box 
                sx={{ 
                  mt: 2, 
                  mb: 3, 
                  width: '100%',
                  height: { xs: '200px', sm: '300px', md: '400px' },
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.01)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  },
                  position: 'relative',
                }}
              >
                {idea.image_url ? (
                  <>
                    <CardMedia
                      component="img"
                      image={idea.image_url}
                      alt={idea.title}
                      sx={{ 
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {isCurrentUserAdmin() && (
                      <IconButton
                        color="primary"
                        aria-label="update image"
                        onClick={handleOpenImageDialog}
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          right: 16,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <PhotoCameraIcon />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <Box 
                    sx={{ 
                      width: '100%',
                      height: '100%',
                      background: getPlaceholderGradient(idea.id),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      position: 'relative',
                    }}
                  >
                    <LightbulbIcon 
                      sx={{ 
                        fontSize: '4rem', 
                        color: 'white',
                        mb: 2,
                        opacity: 0.9,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                      }} 
                    />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'white',
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        fontWeight: 500
                      }}
                    >
                      Visualize your idea here
                    </Typography>
                    {isCurrentUserAdmin() && (
                      <IconButton
                        color="primary"
                        aria-label="upload image"
                        onClick={handleOpenImageDialog}
                        sx={{
                          position: 'absolute',
                          bottom: 16,
                          right: 16,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                            transform: 'scale(1.1)',
                          },
                        }}
                      >
                        <PhotoCameraIcon />
                      </IconButton>
                    )}
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar 
                  sx={{ 
                    mr: 2, 
                    bgcolor: idea.is_anonymous ? '#9e9e9e' : getAvatarColor(idea.user_display_name || idea.id),
                    width: 48,
                    height: 48,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                >
                  {idea.is_anonymous ? 'A' : ((idea.user_display_name && idea.user_display_name.length > 0) ? idea.user_display_name[0].toUpperCase() : 'U')}
                </Avatar>
                <Box>
                  <Typography variant="body1" fontWeight={500}>
                    {idea.is_anonymous ? 'Anonymous' : (idea.user_display_name || 'Unknown User')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Posted on {formatDate(idea.created_at)}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7, mb: 3 }}>
                {idea.tags && idea.tags.map((tag) => (
                  <Chip 
                    key={tag.id} 
                    label={tag.name} 
                    color="primary" 
                    variant="outlined"
                    sx={{
                      borderRadius: '16px',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.08)',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                      }
                    }}
                  />
                ))}
              </Box>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3, 
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.7,
                  color: 'text.primary',
                  fontSize: '1.05rem'
                }}
              >
                {idea.description}
              </Typography>
              
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  pt: 3,
                  mt: 3
                }}
              >
                <Button
                  variant={(idea.liked_by_user || idea.userUpvoted) ? "contained" : "outlined"}
                  color="primary"
                  startIcon={(idea.liked_by_user || idea.userUpvoted) ? <ThumbUpIcon /> : <ThumbUpOutlinedIcon />}
                  onClick={handleUpvote}
                  sx={{ 
                    mr: 2,
                    borderRadius: 8,
                    px: 3,
                    py: 1,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255,255,255,0.1)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.4s ease',
                    },
                    '&:hover::after': {
                      transform: 'translateX(0)'
                    }
                  }}
                >
                  {(idea.liked_by_user || idea.userUpvoted) ? 'Upvoted' : 'Upvote'} ({idea.likes})
                </Button>
                
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {idea.comment_count} comments
                </Typography>
              </Box>
              
              {!idea.approved && (
                <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
                  This idea is awaiting approval from moderators before it will be visible to the public.
                </Alert>
              )}
            </Paper>
          </Zoom>
        </Box>
      </Fade>
      
      <Fade in={animateIn} timeout={800} style={{ transitionDelay: '200ms' }}>
        <Box>
          <CommentsSection 
            comments={comments} 
            ideaId={idea.id}
            loading={commentsLoading}
            onAddComment={handleAddComment}
            onDeleteComment={handleDeleteComment}
          />
        </Box>
      </Fade>
      
      {/* Idea Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            p: 1
          }
        }}
        TransitionComponent={Zoom}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Delete Idea
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', opacity: 0.8 }}>
            Are you sure you want to delete this idea? This will also delete all associated comments and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCancelDelete} 
            color="primary"
            sx={{
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: '0 4px 8px rgba(244,67,54,0.25)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(244,67,54,0.3)',
              }
            }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit Idea Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            p: 1
          }
        }}
        TransitionComponent={Zoom}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Edit Idea
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', opacity: 0.8, mb: 3 }}>
            Update the title and description of this idea.
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            variant="outlined"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            required
            inputProps={{ maxLength: 200 }}
            helperText={`${editTitle.length}/200 characters`}
            sx={{ mb: 2 }}
          />
          
          <TextField
            margin="dense"
            label="Description"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={6}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            inputProps={{ maxLength: 5000 }}
            helperText={`${editDescription.length}/5000 characters`}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseEditDialog} 
            color="primary"
            disabled={editing}
            sx={{
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditIdea} 
            color="primary" 
            variant="contained"
            disabled={!editTitle.trim() || editing}
            startIcon={editing ? <CircularProgress size={20} color="inherit" /> : <EditIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: '0 4px 8px rgba(25, 118, 210, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(25, 118, 210, 0.3)',
              }
            }}
          >
            {editing ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Upload Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={handleCloseImageDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            p: 1
          }
        }}
        TransitionComponent={Zoom}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Update Idea Image
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.primary', opacity: 0.8, mb: 2 }}>
            Choose a new image for this idea. Recommended size is 1200x800 pixels.
          </DialogContentText>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
            <input
              ref={fileInputRef}
              accept="image/*"
              type="file"
              onChange={handleImageSelect}
              style={{ display: 'none' }}
              id="image-upload-input"
            />
            <label htmlFor="image-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<PhotoCameraIcon />}
                sx={{ mb: 2 }}
              >
                Select Image
              </Button>
            </label>
            
            {selectedImage && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Selected: {selectedImage.name}
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 300,
                    maxHeight: 200,
                    overflow: 'hidden',
                    borderRadius: 1,
                    mb: 2,
                  }}
                >
                  <img
                    src={URL.createObjectURL(selectedImage)}
                    alt="Preview"
                    style={{ width: '100%', height: 'auto' }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleCloseImageDialog} 
            color="primary"
            sx={{
              borderRadius: 2,
              px: 3
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleImageUpload} 
            color="primary" 
            variant="contained"
            disabled={!selectedImage || uploadingImage}
            startIcon={uploadingImage ? <CircularProgress size={20} color="inherit" /> : <PhotoCameraIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              boxShadow: '0 4px 8px rgba(25, 118, 210, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(25, 118, 210, 0.3)',
              }
            }}
          >
            {uploadingImage ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default IdeaDetail; 